<template>
  <v-container
    fluid
    class="py-5 px-5 py-sm-15 px-sm-10 fill-height align-start"
  >
    <v-row>
      <v-col>
        <v-row justify="end" align="center">
          <v-col cols="12" class="text-start">
            <span class="textLight--text font-weight-medium text-h5">
              {{ $t("user.registration.attendanceDownload") }}
            </span>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg>
            <BatchesDropDown
              v-model="batch"
              :disabled="loading"
              :returnObject="true"
            />
          </v-col>

          <v-col cols="12" sm="6" md="4" lg>
            <v-autocomplete
              :disabled="isDisabled"
              hide-details
              v-model="organization"
              :items="organizations"
              item-value="_id"
              flat
              solo
              :label="$t('user.registration.organization')"
              class="rounded-lg"
              clearable
              :filter="customLocalisedAutoCompleteFilter"
            >
              <template slot="selection" slot-scope="data">
                {{ getLocalisedValue(data.item.name) }}
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex justify-start">
                  {{ getLocalisedValue(item.name) }}
                </div>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg>
            <v-autocomplete
              :disabled="isDisabled"
              hide-details
              v-model="training"
              :items="courses"
              item-value="_id"
              flat
              solo
              :label="$t('user.registration.training')"
              class="rounded-lg"
              clearable
              :filter="customLocalisedAutoCompleteFilter"
            >
              <template slot="selection" slot-scope="data">
                {{ getLocalisedValue(data.item.title) }}
              </template>
              <template v-slot:item="{ item }">
                <div class="d-flex justify-start">
                  {{ getLocalisedValue(item.title) }}
                </div>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg>
            <v-autocomplete
              :disabled="isDisabled"
              hide-details
              v-model="trainer"
              :items="trainers"
              item-value="_id"
              :item-text="getEmployeeText"
              flat
              solo
              :label="$t('course.trainer')"
              class="rounded-lg"
              clearable
              :filter="customLocalisedAutoCompleteFilter"
            >
              <template v-slot:item="{ item }">
                <div class="d-flex justify-start">
                  {{ `${item.name || ""} ${item.lastName || ""}` }}
                  {{ item.email ? `(${item.email})` : "" }}
                </div>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg>
            <v-menu
              v-model="startDateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
            >
              <template v-slot:activator="{ on, attrs }">
                <v-text-field
                  :disabled="isDisabled"
                  hide-details
                  v-model="formattedStartDate"
                  :label="$t('invoice.chooseFromDate')"
                  class="rounded-lg"
                  flat
                  solo
                  readonly
                  v-bind="attrs"
                  v-on="on"
                  clearable
                >
                  <v-icon slot="append" color="textLight">
                    mdi-calendar-blank-outline
                  </v-icon></v-text-field
                >
              </template>

              <v-date-picker
                color="primary"
                v-model="startDate"
                no-title
                @input="startDateMenu = false"
              ></v-date-picker>
            </v-menu>
          </v-col>

          <v-col cols="12" sm="6" md="4" lg>
            <v-btn
              depressed
              :disabled="btnDisable && !batch"
              :loading="loading"
              block
              height="45"
              color="primary"
              style="text-transform: unset"
              class="rounded-lg font-weight-normal"
              @click="getAttendance"
            >
              {{ $t("user.registration.getAttendance") }}
            </v-btn>
          </v-col>
        </v-row>

        <v-row v-if="isDataGenerated">
          <v-col>
            <AttendanceDownloadTable
              :dataList="data"
              :loading="loading"
              :batch="batch"
              :organization="organizationName"
              :training="trainingName"
              :trainer="trainerName"
              :date="startDate"
              :remarksArr="remarks"
            />
          </v-col>
        </v-row>

        <v-row v-if="isDataGenerated">
          <v-col cols="12" v-if="data && data.length">
            <Pagination :length="totalPages" @onPageSelect="onPagination" />
          </v-col>
        </v-row>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import AttendanceDownloadTable from "../components/AttendanceDownloadTable.vue";
import Pagination from "../../core-module/components/Pagination.vue";
import BatchesDropDown from "@/app/batch-module/components/BatchesDropDown";

export default {
  components: {
    BatchesDropDown,
    AttendanceDownloadTable,
    Pagination,
  },
  data() {
    return {
      loading: false,
      allAttendanceData: [],
      data: [],
      totalCount: 0,
      courses: [],
      organizations: [],
      employees: [],
      trainerId: null,
      batch: null,
      organization: null,
      organizationName: null,
      training: null,
      trainingName: null,
      trainer: null,
      trainerName: null,
      startDate: null,
      startDateMenu: false,
      btnDisable: true,
      isDataGenerated: false,
      filterInputs: {},
      variables: this.defaultVariables(),
      remarks: [],
      totalPages: 0,
    };
  },
  computed: {
    isDisabled() {
      return this.loading === true || this.batch?._id != null;
    },
    trainers() {
      return this.trainerId && this.employees?.length
        ? this.employees
            .map(obj => {
              let result = obj.subRoles.find(
                role => role._id === this.trainerId
              );
              if (result) return { ...obj };
            })
            .filter(filterObj => filterObj !== undefined)
        : [];
    },
    formattedStartDate: {
      get() {
        return this.startDate
          ? this.$moment(this.startDate).format("DD/MM/YYYY")
          : "";
      },
      set(value) {
        this.startDate = value;
      },
    },
  },
  apollo: {
    getTrainings() {
      return {
        query: require("../../course-module/api/query/getTrainings.gql"),
        variables: { limit: 0, skip: 0 },
        result({ data, loading }) {
          if (data && data.getTrainings && data.getTrainings.trainings) {
            this.courses = data.getTrainings.trainings?.filter(
              x => x.isActive === true && x.approvalStatus === "APPROVED"
            );
          }
        },
      };
    },
    getOrganizations() {
      return {
        query: require("../../organization-module/api/query/getOrganizations.gql"),
        variables: { limit: 0, skip: 0, filterInput: null },
        result({ data, loading }) {
          if (
            data &&
            data.getOrganizations &&
            data.getOrganizations.organizations
          ) {
            this.organizations = data.getOrganizations.organizations?.filter(
              x => x.isActive === true
            );
          }
        },
      };
    },
    getEmployees() {
      return {
        query: require("../api/query/getEmployees.gql"),
        variables: {
          limit: 0,
          skip: 0,
        },
        result({ data, loading }) {
          if (data?.getEmployees) {
            this.employees = data.getEmployees.employees.map(employee => {
              let obj = {};
              obj._id = employee._id;
              obj.organization = employee?.organization?._id;
              obj.name = employee?.user?.firstName;
              obj.lastName = employee?.user?.lastName;
              obj.email = employee?.user?.email;
              obj.civilIdOrPassport = employee?.user?.civilIdOrPassport;
              obj.subRoles = employee?.user?.subRoles
                ? employee.user.subRoles
                : [];
              return obj;
            });
          }
        },
      };
    },
    getSubRoles() {
      return {
        query: require("../api/query/getSubRoles.gql"),
        variables: { limit: 0, skip: 0 },
        result({ data, loading }) {
          if (data && data.getSubRoles && data.getSubRoles.subRoles) {
            this.trainerId = data.getSubRoles.subRoles.find(
              x => x.name === "TRAINER"
            )?._id;
          }
        },
      };
    },
    getTrainingRegistrationAttendances() {
      return {
        query: require("../../course-module/api/query/getTrainingRegistrationAttendances.gql"),
        variables: { limit: 0, skip: 0 },
        skip: () => true,
        result({ data, loading }) {
          if (
            data &&
            data.getTrainingRegistrationAttendances &&
            data.getTrainingRegistrationAttendances
              .trainingRegistrationAttendance
          ) {
            let resData =
              data.getTrainingRegistrationAttendances
                .trainingRegistrationAttendance;
            this.allAttendanceData = Array.from(
              new Set(resData.map(obj => obj?.employee?.user?._id))
            ).map(id => {
              return resData.find(val => val?.employee?.user?._id === id);
            });
            this.data = this.paginationLocally(0, this.variables.limit);

            this.totalCount =
              data.getTrainingRegistrationAttendances.totalCount;
            this.remarks = [
              ...new Set(
                data.getTrainingRegistrationAttendances.trainingRegistrationAttendance
                  .map(obj => {
                    if (obj?.remarks && obj?.remarks !== "") {
                      return obj.remarks;
                    }
                  })
                  .filter(ele => ele !== undefined)
              ),
            ];
            let pagesVal = this.totalCount / this.defaultVariables().limit;
            this.totalPages = this.checkNumberIsFloat(pagesVal)
              ? Math.ceil(pagesVal)
              : pagesVal;
          }
          this.loading = loading;
        },
      };
    },
  },
  watch: {
    batch() {
      this.filterBy("batch", this.batch?._id);
    },
    organization() {
      if (this.organization && this.organizations.length) {
        this.organizationName = this.getLocalisedValue(
          this.organizations.find(obj => obj._id === this.organization)?.name
        );
      } else this.organizationName = null;

      this.filterBy("organization", this.organization);
    },
    training() {
      if (this.training && this.courses.length) {
        this.trainingName = this.getLocalisedValue(
          this.courses.find(obj => obj._id === this.training)?.title
        );
      } else this.trainingName = null;
      this.filterBy("training", this.training);
    },
    trainer() {
      if (this.trainer && this.trainers.length) {
        this.trainerName = this.getEmployeeText(
          this.trainers.find(obj => obj._id === this.trainer)
        );
      } else this.trainerName = null;
      this.filterBy("trainer", this.trainer);
    },
    startDate() {
      if (this.startDate) {
        this.filterBy("date", this.startDate);
      } else {
        this.filterBy("date", this.startDate);
      }
    },
  },
  methods: {
    defaultVariables() {
      return {
        limit: 10,
        skip: 0,
      };
    },
    checkNumberIsFloat(value) {
      return Number(value) === value && value % 1 !== 0;
    },
    getEmployeeText(item) {
      return `${item.name || ""} ${item.lastName || ""}`;
    },
    onPagination(val) {
      if (val > 0) {
        this.variables.skip = this.variables.limit * (val - 1);
        this.data = this.paginationLocally(
          this.variables.skip,
          this.variables.limit + this.variables.skip
        );
      }
    },
    paginationLocally(start, end) {
      return this.allAttendanceData.slice(start, end);
    },
    filterBy(key, value) {
      if (key === "batch") {
        if (!value) {
          delete this.filterInputs?.batch;
        } else {
          this.filterInputs.batch = value;
        }
      }

      if (key === "training") {
        if (!value) {
          delete this.filterInputs?.training;
        } else {
          this.filterInputs.training = value;
        }
      }

      if (key === "organization") {
        if (!value) {
          delete this.filterInputs?.organization;
        } else {
          this.filterInputs.organization = value;
        }
      }

      if (key === "date") {
        if (!value) {
          delete this.filterInputs?.startDate;
        } else {
          this.filterInputs.startDate = value;
        }
      }

      if (key === "trainer") {
        if (!value) {
          delete this.filterInputs?.trainer;
        } else {
          this.filterInputs.trainer = value;
        }
      }

      if (
        this.filterInputs?.training &&
        this.filterInputs?.organization &&
        this.filterInputs?.trainer &&
        this.filterInputs?.startDate
      ) {
        this.btnDisable = false;
        this.isDataGenerated = false;
      } else {
        this.isDataGenerated = false;
        this.btnDisable = true;
      }
    },
    getAttendance() {
      this.variables = this.defaultVariables();
      this.refetch();
      this.isDataGenerated = true;
    },
    async refetch() {
      this.loading = true;
      await this.$apollo.queries.getTrainingRegistrationAttendances.refetch({
        limit: 0,
        skip: 0,
        filterInput: this.filterInputs,
      });
      this.$apollo.queries.getTrainingRegistrationAttendances.skip = false;
    },
  },
};
</script>
